import { determineDataSource } from "../../../Data/DataStores/DataSource";
import Cloner from "../../Generic/Cloner";
import UUID from "../../Generic/UUID";
import { DISABLE_EMBEDDED } from "../CurrentRequest";
import { type ApplicationAgent } from "./ApplicationAgent";
import {
    type ApplicationEventsFromRedux,
    type ApplicationEventsFromShopfront,
    ApplicationEventsToShopfront,
} from "./ApplicationEvents";
import { createNewApplicationAgentForManager } from "./ApplicationUtilities";
import { UnsupportedApplicationEventError } from "./UnsupportedApplicationEventError";

export type ApplicationManagerEventData<T> = {
    application: ApplicationAgent;
    data: T;
};

export type ApplicationManagerSendData<T> = Array<{
    application: string;
    data: T;
}>;

export interface PipelineOptions {
    noDataTimeoutPerPipe: number;
    earlyReturnValue: unknown;
    ignoreNotReadyApplications: boolean;
}

export type ApplicationManagerEventCallback<T> = (data: ApplicationManagerEventData<T>) => void;

export const InternalApplicationEvents = {
    APPLICATION_CREATED  : "APPLICATION_CREATED",
    APPLICATION_DESTROYED: "APPLICATION_DESTROYED",
};

export type ApplicationEventTypes = typeof InternalApplicationEvents[keyof typeof InternalApplicationEvents] |
    typeof ApplicationEventsToShopfront.LISTENERS[keyof typeof ApplicationEventsToShopfront.LISTENERS];

export class ApplicationManager {
    protected applications: Record<string, ApplicationAgent> = {};
    protected eventListeners: Record<ApplicationEventTypes, Array<ApplicationManagerEventCallback<any>>> = {};
    protected pipelineListeners = new Map<string, {
        event: ApplicationEventsFromShopfront;
        callback: (data: any) => void;
        baseData: unknown;
        context: Record<string, unknown> | object;
        timeout: number;
    }>();

    protected dataCache: Record<string, Array<{
        application: string;
        data: unknown;
    }>> = {};

    constructor() {
        this.setupEventListeners();
    }

    public setup(): void {
        this.mountListeners();

        if(DISABLE_EMBEDDED) {
            return;
        }

        determineDataSource()
            .then(source => {
                return source.applications.all();
            })
            .then(applications => {
                for(let i = 0, l = applications.length; i < l; i++) {
                    this.createApplication(applications[i]);
                }
            });
    }

    public destroy(): void {
        this.unmountListeners();

        for(const i in this.applications) {
            if(!Object.prototype.hasOwnProperty.call(this.applications, i)) continue;

            this.applications[i].destroy();
        }
    }

    protected mountListeners(): void {
        document.addEventListener("synchronisationFinished", this.update);
    }

    protected unmountListeners(): void {
        document.removeEventListener("synchronisationFinished", this.update);
    }

    protected setupEventListeners(): void {
        this.eventListeners = {
            [ApplicationEventsToShopfront.LISTENERS.SELL_SCREEN_OPTION_CHANGE]       : [],
            [ApplicationEventsToShopfront.LISTENERS.INTERNAL_PAGE_MESSAGE]           : [],
            [ApplicationEventsToShopfront.LISTENERS.TABLE_UPDATE]                    : [],
            [ApplicationEventsToShopfront.LISTENERS.SELL_SCREEN_PROMOTION_APPLICABLE]: [],
            [InternalApplicationEvents.APPLICATION_CREATED]                          : [],
            [InternalApplicationEvents.APPLICATION_DESTROYED]                        : [],
        };
    }

    public addEventListener<T>(event: ApplicationEventTypes, callback: ApplicationManagerEventCallback<T>): void {
        if(typeof this.eventListeners[event] === "undefined") {
            throw new Error("Invalid event provided to ApplicationManager");
        }

        this.eventListeners[event].push(callback);
    }

    public removeEventListener<T>(event: ApplicationEventTypes, callback: ApplicationManagerEventCallback<T>): void {
        if(typeof this.eventListeners[event] === "undefined") {
            throw new Error("Invalid event provided to ApplicationManager");
        }

        for(let i = 0, l = this.eventListeners[event].length; i < l; i++) {
            if(this.eventListeners[event][i] === callback) {
                this.eventListeners[event] = [
                    ...this.eventListeners[event].slice(0, i),
                    ...this.eventListeners[event].slice(i + 1),
                ];

                return;
            }
        }
    }

    public emitEventFromApplication(
        event: string,
        application: ApplicationAgent,
        data: Record<string, any>
    ): void {
        if(event === ApplicationEventsToShopfront.LISTENERS.PIPELINE_TRIGGER) {
            const details = this.pipelineListeners.get(data.id);

            if(typeof details !== "undefined") {
                this.triggerPipeline(
                    details.event,
                    details.baseData,
                    details.context,
                    details.timeout
                )
                    .then(details.callback);
            }

            return;
        }

        if(typeof this.eventListeners[event] === "undefined") {
            throw new Error("Unknown event provided to ApplicationManager from ApplicationAgent");
        }

        const eventData = {
            application,
            data,
        };

        // Freeze the event so no listener changes it.
        Object.freeze(eventData);

        for(let i = 0, l = this.eventListeners[event].length; i < l; i++) {
            this.eventListeners[event][i](eventData);
        }
    }

    public update = async (): Promise<void> => {
        if(DISABLE_EMBEDDED) {
            return;
        }

        // Check to see if the applications have changed
        const source = await determineDataSource();
        const applications = await source.applications.all();

        const matched = [];
        let hasUpdates = false;

        for(let i = 0, l = applications.length; i < l; i++) {
            matched.push(applications[i].id);

            if(typeof this.applications[applications[i].id] === "undefined") {
                this.createApplication(applications[i]);
                hasUpdates = true;
                continue;
            }

            this.applications[applications[i].id].update(
                applications[i].name,
                applications[i].embedded,
                applications[i].auth,
                applications[i].expiryDate
            );
        }

        for(const i in this.applications) {
            if(!Object.prototype.hasOwnProperty.call(this.applications, i)) continue;

            if(matched.includes(i)) {
                continue;
            }

            this.destroyApplication(i);
            hasUpdates = true;
        }

        if(hasUpdates) {
            this.clearDataCache();
        }
    };

    protected createApplication(application: {
        embedded: false | string;
        id: string;
        name: string;
        auth: string;
        expiryDate: string;
    }): void {
        if(DISABLE_EMBEDDED) {
            return;
        }

        if(!application.embedded) {
            return;
        }

        if(typeof this.applications[application.id] !== "undefined") {
            // Already created, potential due to race condition between sync and database ready
            return;
        }

        this.applications[application.id] = createNewApplicationAgentForManager(
            application.id,
            application.name,
            application.embedded,
            application.auth,
            application.expiryDate
        );

        this.emitEventFromApplication(
            InternalApplicationEvents.APPLICATION_CREATED,
            this.applications[application.id],
            {}
        );
    }

    protected destroyApplication(id: string): void {
        this.emitEventFromApplication(
            InternalApplicationEvents.APPLICATION_DESTROYED,
            this.applications[id],
            {}
        );

        this.applications[id].destroy();
        delete this.applications[id];
    }

    protected determineCacheName(
        event: ApplicationEventsFromShopfront | ApplicationEventsFromRedux,
        data: unknown
    ): string {
        let cacheName: string;

        if(typeof data !== "object") {
            cacheName = `${event}|${data}`;
        } else {
            cacheName = `${event}|${JSON.stringify(data)}`;
        }

        return cacheName;
    }

    public send<T>(
        event: ApplicationEventsFromShopfront,
        data: unknown,
        defaultData: T,
        noDataTimeout: number,
        cacheResult: true
    ): Promise<[cached: ApplicationManagerSendData<T>, resolved: Promise<ApplicationManagerSendData<T>>]>;
    public send<T>(
        event: ApplicationEventsFromShopfront | ApplicationEventsFromRedux,
        data: unknown,
        defaultData?: T,
        noDataTimeout?: number,
        cacheResult?: false
    ): Promise<ApplicationManagerSendData<T>>;
    public send<T>(
        event: ApplicationEventsFromShopfront | ApplicationEventsFromRedux,
        data: unknown,
        defaultData?: T,
        noDataTimeout = 30000,
        cacheResult = false
    ): Promise<
        ApplicationManagerSendData<T> | [
            cached: ApplicationManagerSendData<T>,
            resolved: Promise<ApplicationManagerSendData<T>>,
        ]
    > {
        let cacheName: false | string = false;

        if(cacheResult) {
            cacheName = this.determineCacheName(event, data);
        }

        let cachedData: ApplicationManagerSendData<T> = [];
        let loadIntoCache = false;

        if(cacheName !== false && typeof this.dataCache[cacheName] !== "undefined") {
            cachedData = this.dataCache[cacheName] as ApplicationManagerSendData<T>;
        } else if(cacheName !== false) {
            loadIntoCache = true;
        }

        const results: Array<Promise<undefined | {
            application: string;
            data: T;
        }>> = [];

        for(const i in this.applications) {
            if(!Object.prototype.hasOwnProperty.call(this.applications, i)) {
                continue;
            }

            if(loadIntoCache) {
                cachedData.push({
                    application: this.applications[i].id,
                    data       : defaultData as T,
                });
            }

            if(!this.applications[i].isReady()) {
                continue;
            }

            const callback = (res: (data: undefined | {
                application: string;
                data: T;
            }) => void): void => {
                let hasResult = false;

                const timeout = setTimeout(() => {
                    hasResult = true;
                    res(typeof defaultData === "undefined" ? undefined : {
                        application: this.applications[i].id,
                        data       : defaultData,
                    });
                }, noDataTimeout);

                this.applications[i].send<T>(event, data)
                    .then(results => {
                        if(hasResult) {
                            return;
                        }

                        hasResult = true;

                        clearTimeout(timeout);
                        res(results);
                    })
                    .catch(err => {
                        if(err instanceof UnsupportedApplicationEventError) {
                            res(typeof defaultData === "undefined" ? undefined : {
                                application: this.applications[i].id,
                                data       : defaultData,
                            });
                        } else {
                            console.error("Received an unknown error from an application", err);
                        }
                    });
            };

            results.push(new Promise(callback));
        }

        const promises = Promise.all(results)
            .then(results => {
                const filtered = results.filter(result => typeof result !== "undefined") as Array<{
                    application: string;
                    data: T;
                }>;

                return filtered.flat();
            })
            .then(results => {
                if(cacheName !== false) {
                    this.dataCache[cacheName] = results;
                }

                return results;
            });

        if(cacheResult) {
            return Promise.resolve([ cachedData, promises ]);
        }

        return promises;
    }

    /**
     * Send an event through each application one-by-one, passing the
     * previous result to the next pipeline
     */
    private async triggerPipeline<TBaseData, TContext extends Record<string, unknown> | object>(
        event: ApplicationEventsFromShopfront,
        data: TBaseData,
        context: TContext,
        noDataTimeoutPerPipe?: number,
        earlyReturnValue?: unknown,
        pipelineId?: string,
        ignoreNotReadyApplication = false
    ): Promise<TBaseData> {
        let pipelineObject = data;

        for(const i in this.applications) {
            if(!Object.prototype.hasOwnProperty.call(this.applications, i)) {
                continue;
            }

            if(!ignoreNotReadyApplication && !this.applications[i].isReady()) {
                await new Promise(res => {
                    setTimeout(res, 5000);
                });
            }

            if(!this.applications[i].isReady()) {
                continue;
            }

            let timeoutId: ReturnType<typeof setTimeout>;

            const potentialResults = [
                this.applications[i].send<TBaseData>(event, { data: pipelineObject, context, pipelineId })
                    .then(results => {
                        clearTimeout(timeoutId);

                        return results.data;
                    })
                    .catch(err => {
                        clearTimeout(timeoutId);

                        if(!(err instanceof UnsupportedApplicationEventError)) {
                            console.error("Received an unknown error from an application", err);
                        }

                        return pipelineObject;
                    }),
            ];

            if(typeof noDataTimeoutPerPipe === "number" && isFinite(noDataTimeoutPerPipe)) {
                potentialResults.push(
                    new Promise<TBaseData>(res => {
                        timeoutId = setTimeout(() => {
                            res(pipelineObject);
                        }, noDataTimeoutPerPipe);
                    })
                );
            }

            pipelineObject = await Promise.race<TBaseData>(potentialResults);

            if(typeof earlyReturnValue !== "undefined" && pipelineObject === earlyReturnValue) {
                return pipelineObject;
            }
        }

        return pipelineObject;
    }

    /**
     * Send an event through each application one-by-one, passing the
     * previous result to the next pipeline
     */
    public sendPipeline<TBaseData, TContext extends Record<string, unknown> | object>(
        event: ApplicationEventsFromShopfront,
        data: TBaseData,
        context: TContext,
        onUpdate: (data: TBaseData) => void,
        options?: Partial<PipelineOptions>
    ): () => void;
    public sendPipeline<TBaseData, TContext extends Record<string, unknown> | object>(
        event: ApplicationEventsFromShopfront,
        data: TBaseData,
        context: TContext,
        options?: Partial<PipelineOptions>
    ): Promise<TBaseData>;
    public sendPipeline<TBaseData, TContext extends Record<string, unknown> | object>(
        event: ApplicationEventsFromShopfront,
        data: TBaseData,
        context: TContext,
        optionsOrUpdateCallback?: Partial<PipelineOptions> | ((data: TBaseData) => void),
        options?: Partial<PipelineOptions>
    ): Promise<TBaseData> | (() => void) {
        let callback;
        let pipelineId: string | undefined;

        const defaultOptions: PipelineOptions = {
            noDataTimeoutPerPipe      : 30000,
            earlyReturnValue          : undefined,
            ignoreNotReadyApplications: false,
        };

        if(typeof optionsOrUpdateCallback === "function") {
            callback = optionsOrUpdateCallback;
            pipelineId = UUID.generate();

            const optionsToUse = {
                ...defaultOptions,
                ...options,
            };

            this.pipelineListeners.set(pipelineId, {
                callback,
                event,
                context,
                baseData: Cloner.Clone(data),
                timeout : optionsToUse.noDataTimeoutPerPipe,
            });

            this.triggerPipeline(
                event,
                data,
                context,
                optionsToUse.noDataTimeoutPerPipe,
                optionsToUse.earlyReturnValue,
                pipelineId,
                optionsToUse.ignoreNotReadyApplications
            )
                .then(callback);

            return () => {
                this.pipelineListeners.delete(pipelineId as string);
            };
        }

        const optionsToUse = {
            ...defaultOptions,
            ...options,
            ...optionsOrUpdateCallback,
        };

        return this.triggerPipeline(
            event,
            data,
            context,
            optionsToUse.noDataTimeoutPerPipe,
            optionsToUse.earlyReturnValue,
            undefined,
            optionsToUse.ignoreNotReadyApplications
        );
    }

    public hasApplications(): boolean {
        for(const i in this.applications) {
            if(!Object.prototype.hasOwnProperty.call(this.applications, i)) {
                continue;
            }

            return true;
        }

        return false;
    }

    public getApplication(clientId: string): null | ApplicationAgent {
        if(typeof this.applications[clientId] === "undefined") {
            return null;
        }

        return this.applications[clientId];
    }

    public fromDataCache<T>(
        event: ApplicationEventsFromShopfront | ApplicationEventsFromRedux,
        data: unknown
    ): Array<{
        application: string;
        data: T;
    }> {
        const cacheName = this.determineCacheName(event, data);

        if(typeof this.dataCache[cacheName] === "undefined") {
            return [];
        }

        return this.dataCache[cacheName] as Array<{
            application: string;
            data: T;
        }>;
    }

    public clearDataCache(): void {
        this.dataCache = {};
    }
}

